import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { jsonLdScript } from "@/lib/jsonld";
import { webPageScript } from "@/lib/webpage-jsonld";
import { ExternalLink, Zap, Home, FileCheck, RefreshCw } from "lucide-react";

const TITLE = "e-Return vs Paper Return | কোনটি ভালো, কে কে বাধ্যতামূলক";
const DESC = "২০২৫-২৬ কর বছরে অনলাইন রিটার্ন কার জন্য বাধ্যতামূলক, এবং অনলাইন রিটার্নের সুবিধা কি জানুন।";
const URL = "https://onlinetaxvat.com/e-return-vs-paper-return";

const BENEFITS = [
  { Icon: Home, color: "bg-emerald-500", t: "ঘরে বসে জমা দেওয়া যায়", d: "যেকোনো সময়, যেকোনো ডিভাইস থেকে রিটার্ন সাবমিট করা সম্ভব।" },
  { Icon: Zap, color: "bg-amber-500", t: "সাথে সাথে assessment", d: "সিস্টেম স্বয়ংক্রিয়ভাবে কর হিসাব করে assessment সম্পন্ন করে।" },
  { Icon: FileCheck, color: "bg-blue-500", t: "তাৎক্ষণিক Acknowledgement ও Tax Certificate", d: "জমা দেওয়ার সাথে সাথে PDF আকারে ডাউনলোড করা যায়।" },
  { Icon: RefreshCw, color: "bg-purple-500", t: "আগের বছরের তথ্য Autofill", d: "পুনরাবৃত্তি কমে, ভুলের সম্ভাবনা কম, সময় সাশ্রয় হয়।" },
];

export const Route = createFileRoute("/e-return-vs-paper-return")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      webPageScript({ path: "/e-return-vs-paper-return", name: TITLE, description: DESC }),
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "e-Return vs পেপার রিটার্ন — কোনটা বেছে নেবেন",
        description: DESC,
        inLanguage: "bn",
        mainEntityOfPage: URL,
        author: { "@type": "Organization", name: "Online Tax VAT Bangladesh" },
        publisher: { "@type": "Organization", name: "Online Tax VAT Bangladesh" },
      }),
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <article className="container-page max-w-3xl py-14 md:py-20">
        <header className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">
            e-Return vs Paper Return
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">
            e-Return vs পেপার রিটার্ন — কোনটা বেছে নেবেন
          </h1>
        </header>

        <section className="glass rounded-3xl border border-border/60 p-6 md:p-8 shadow-card space-y-4 text-[15px] leading-relaxed">
          <p>
            <strong>২০২৫-২৬ কর বছর</strong> থেকে সকল স্বাভাবিক করদাতার জন্য অনলাইনে রিটার্ন দাখিল বাধ্যতামূলক করা হয়েছে (কিছু ব্যতিক্রম যেমন প্রবীণ বা প্রবাসী করদাতা ছাড়া)। ফলে পেপার রিটার্নের চেয়ে e-Return এখন প্রধান পদ্ধতি।
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-xl md:text-2xl font-bold mb-6">অনলাইন রিটার্নের সুবিধা</h2>
          <ul className="grid sm:grid-cols-2 gap-4">
            {BENEFITS.map(({ Icon, color, t, d }, i) => (
              <li key={i} className="p-5 rounded-2xl border border-border/60 bg-card">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`${color} h-10 w-10 rounded-full flex items-center justify-center text-white shadow-sm`}>
                    <Icon className="h-5 w-5" strokeWidth={2.25} />
                  </span>
                  <h3 className="font-display font-bold text-base">{t}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 p-6 rounded-2xl border border-border/60 bg-card">
          <div>
            <h3 className="font-display font-bold">আরও জানুন</h3>
            <Link to="/knowledge-hub/$slug" params={{ slug: "faq-e-return" }} className="text-primary hover:underline text-sm">
              → e-Return ফাইলিং সংক্রান্ত ৪৫টি সচরাচর জিজ্ঞাসা
            </Link>
          </div>
          <a
            href="https://etaxnbr.gov.bd"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition shadow-md"
          >
            etaxnbr.gov.bd এ যান <ExternalLink className="h-4 w-4" />
          </a>
        </section>
        <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["e-return-vs-paper-return"]} />
      </article>
    </SiteShell>
  );
}
